import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  ArrowDown, ArrowUp, Check, ChevronRight, Pencil, Plus,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  createHelpQuestion, loadHelpCentre, loadTranslationCatalogue, setHelpQuestionOrder, setHelpQuestionShown,
  setHelpSectionShown,
} from './api';
import {
  Alert, Empty, Failed, Head, Loading, Pill, SearchField,
} from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { Switch } from './ProductOptions';
import {
  ENGLISH, SITE_TEXT, Words, activeLanguages, ownWords, saveTexts, unix, wordsOf,
} from './siteWords';
import SiteLanguage from './SiteLanguage';
import { ForStoreOffice, StoreOfficeLine } from './StoreOffice';
import './productDetail.css';
import './help-centre.css';

// The storefront's help centre, written here.
//
// It is laid out the way the store shows it: the first page's own short list,
// then the topics, each with its groups of questions and, for some, its
// guides (one per test or product), which have groups of their own. XOmics
// keeps which questions there are, their order and which are shown
// (/HelpCentre). Their words are storefront site text, read and written
// through the translation endpoints, one language at a time, like a
// product's page: English first, and any other language beside it, where an
// empty text shows the English on the store.
//
// Which questions there are, their order and which are shown is the same for
// every language: the language chosen only decides whose words are read and
// written. A new question is written in English first, because English is
// what every language shows until it has words of its own. Nothing is
// deleted: a question, a group, a guide or a topic that is not wanted is
// switched off, and can be switched on again.

const MAX_QUESTION = 300;
const MAX_ANSWER = 4000;
const MAX_RESULTS = 60;

// A topic's and a guide's own words, in the order the store shows them.
const TOPIC_TEXTS = ['name', 'title', 'description', 'cardName', 'cardDescription', 'guidesLabel', 'guidesKind'];
const GUIDE_TEXTS = ['name', 'description'];
// A text the store does without: a topic's card shows its name when it has no
// name of its own.
const OPTIONAL_TEXTS = new Set(['cardName']);
const TEXT_LABEL = {
  name: 'ui.help_centre.text.name',
  title: 'ui.help_centre.text.title',
  description: 'ui.help_centre.text.description',
  cardName: 'ui.help_centre.text.card_name',
  cardDescription: 'ui.help_centre.text.card_description',
  guidesLabel: 'ui.help_centre.text.guides_label',
  guidesKind: 'ui.help_centre.text.guides_kind',
};

// What the store calls a topic or a guide: a topic goes by the name on its
// card where it has one.
const nameOf = (section, language) => wordsOf(section.texts.cardName, language)
  || wordsOf(section.texts.name, language) || section.code;
// How much of a question another language has: 'all', 'part' or 'none'.
function writtenIn(question, language) {
  const has = [question.question, question.answer].filter((text) => ownWords(text, language) !== '').length;
  return has === 2 ? 'all' : has === 1 ? 'part' : 'none';
}

function questionsOf(section) {
  return [
    ...section.groups.flatMap((group) => group.questions),
    ...(section.guides || []).flatMap((guide) => guide.groups.flatMap((group) => group.questions)),
  ];
}

function countLabel(questions, language) {
  const parts = [questions.length === 1 ? tx('ui.help_centre.count_one') : tx('ui.help_centre.count', { count: questions.length })];
  const hidden = questions.filter((question) => !question.isActive).length;
  if (hidden > 0) parts.push(tx('ui.help_centre.count_hidden', { count: hidden }));
  if (language !== ENGLISH) {
    const open = questions.filter((question) => writtenIn(question, language) !== 'all').length;
    if (open > 0) parts.push(tx('ui.help_centre.count_untranslated', { count: open }));
  }
  return parts.join(' · ');
}

// A question being written: a stored one being reworded in the language the
// page is in, or a new one for a group (or for the first page's list), which
// is written in English whatever language the page is in. For another
// language's words the English stands above the fields, and a field left
// empty shows the English on the store.
function QuestionForm({
  question, sectionId, writing, onCancel, onSaved, onDirtyChange, onFailed,
}) {
  const { language } = writing;
  const initial = useMemo(() => ({
    question: ownWords(question?.question, language), answer: ownWords(question?.answer, language),
  }), [question, language]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = form.question !== initial.question || form.answer !== initial.answer;
  const id = question ? `help-question-${question.id}` : `help-question-new-${sectionId}`;
  const english = language === ENGLISH || !question;

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  async function submit(event) {
    event.preventDefault();
    setError('');
    const next = { question: unix(form.question), answer: unix(form.answer) };
    if (english && (next.question === '' || next.answer === '')) {
      setError(tx('ui.help_centre.complete_fields'));
      return;
    }
    setBusy(true);
    try {
      if (!question) {
        await createHelpQuestion({ sectionId, question: next.question, answer: next.answer });
        await onSaved(tx('ui.help_centre.added'));
        return;
      }
      // Each text is its own row: only what was reworded is sent.
      const changes = ['question', 'answer'].filter((name) => next[name] !== initial[name])
        .map((name) => [question[name], next[name]]);
      await saveTexts(changes, writing);
      await onSaved(changes.length > 0 ? tx('ui.help_centre.saved') : tx('ui.help_centre.no_change'));
    } catch (requestError) {
      setError(errorMessage(requestError));
      setBusy(false);
      // One text may have gone through before the other failed: the list shows it.
      onFailed();
    }
  }

  return <form className="xo-pm-card xo-help-form" onSubmit={submit} aria-labelledby={`${id}-title`}>
    <h3 id={`${id}-title`}>{question ? tx('ui.help_centre.edit_title') : tx('ui.help_centre.new_title')}</h3>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {!question && language !== ENGLISH && <p className="xo-pm-hint">{tx('ui.help_centre.new_in_english')}</p>}
    {!english && <div className="xo-help-english">
      <strong>{tx('ui.help_centre.in_english')}</strong>
      <p dir="auto">{question.question.value}</p>
      <Words text={question.answer.value} className="xo-help-answer" />
    </div>}
    <Field id={`${id}-q`} label={tx('ui.help_centre.question')}>
      <input {...fieldAria(`${id}-q`)} type="text" dir="auto" value={form.question} maxLength={MAX_QUESTION}
        placeholder={english ? undefined : question.question.value} disabled={busy}
        onChange={(event) => setForm((current) => ({ ...current, question: event.target.value }))} />
    </Field>
    <Field id={`${id}-a`} label={tx('ui.help_centre.answer')}>
      <textarea {...fieldAria(`${id}-a`)} rows="7" dir="auto" value={form.answer} maxLength={MAX_ANSWER}
        placeholder={english ? undefined : question.answer.value} disabled={busy}
        onChange={(event) => setForm((current) => ({ ...current, answer: event.target.value }))} />
    </Field>
    <div className="xo-help-form-actions">
      <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>{tx('ui.help_centre.cancel')}</button>
      <button type="submit" className="xo-btn" disabled={busy}>
        {busy ? tx('ui.help_centre.saving') : tx('ui.help_centre.save')}</button>
    </div>
  </form>;
}

// Names and lines being written: a group's name, or a topic's or a guide's
// own words. `fields` are [name, text] pairs, each text a stored English row.
function TextsForm({
  id, title, fields, writing, onCancel, onSaved, onDirtyChange, onFailed,
}) {
  const { language } = writing;
  const initial = useMemo(() => Object.fromEntries(fields.map(([name, text]) => [name, ownWords(text, language)])),
    [fields, language]);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = fields.some(([name]) => form[name] !== initial[name]);
  const english = language === ENGLISH;

  useEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);

  async function submit(event) {
    event.preventDefault();
    setError('');
    const next = Object.fromEntries(fields.map(([name]) => [name, unix(form[name])]));
    if (english && fields.some(([name]) => next[name] === '' && !OPTIONAL_TEXTS.has(name))) {
      setError(tx('ui.help_centre.complete_texts'));
      return;
    }
    setBusy(true);
    try {
      const changes = fields.filter(([name]) => next[name] !== initial[name]).map(([name, text]) => [text, next[name]]);
      await saveTexts(changes, writing);
      await onSaved(changes.length > 0 ? tx('ui.help_centre.saved') : tx('ui.help_centre.no_change'));
    } catch (requestError) {
      setError(errorMessage(requestError));
      setBusy(false);
      onFailed();
    }
  }

  return <form className="xo-pm-card xo-help-form" onSubmit={submit} aria-labelledby={`${id}-title`}>
    <h3 id={`${id}-title`}>{title}</h3>
    {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    {fields.map(([name, text]) => <Field key={name} id={`${id}-${name}`} label={tx(TEXT_LABEL[name])}>
      <input {...fieldAria(`${id}-${name}`)} type="text" dir="auto" value={form[name]}
        maxLength={MAX_QUESTION} placeholder={english ? undefined : text.value} disabled={busy}
        onChange={(event) => setForm((current) => ({ ...current, [name]: event.target.value }))} />
    </Field>)}
    <div className="xo-help-form-actions">
      <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>{tx('ui.help_centre.cancel')}</button>
      <button type="submit" className="xo-btn" disabled={busy}>
        {busy ? tx('ui.help_centre.saving') : tx('ui.help_centre.save')}</button>
    </div>
  </form>;
}

// One question: its words, whether the store shows it, and its tools. The
// answer opens under it, drawn as the store draws it. `move` is absent where
// the order is not this list's to set (search results).
function QuestionRow({
  question, language, crumb, mayManage, locked, shown, move, onEdit, onToggle,
}) {
  const [open, setOpen] = useState(false);
  const name = wordsOf(question.question, language) || tx('ui.help_centre.missing_text');
  const written = Boolean(question.question && question.answer);
  const translated = language === ENGLISH ? 'all' : writtenIn(question, language);
  return <li className={`xo-help-row${shown ? '' : ' is-off'}`}>
    <div className="xo-help-row-head">
      <button type="button" className={`xo-help-q${open ? ' is-open' : ''}`} aria-expanded={open}
        onClick={() => setOpen((current) => !current)}>
        <ChevronRight aria-hidden="true" /><span dir="auto">{name}</span>
      </button>
      {translated !== 'all' && <Pill tone="info">{tx(translated === 'part'
        ? 'ui.help_centre.partly_translated' : 'ui.help_centre.not_translated')}</Pill>}
      {!shown && <Pill tone="flat">{tx('ui.help_centre.hidden')}</Pill>}
      {mayManage && <div className="xo-help-tools">
        {move && <>
          <button type="button" className="xo-btn-icon" disabled={locked || move.first}
            aria-label={tx('ui.help_centre.move_up', { name })} onClick={() => move.by(-1)}>
            <ArrowUp aria-hidden="true" /></button>
          <button type="button" className="xo-btn-icon" disabled={locked || move.last}
            aria-label={tx('ui.help_centre.move_down', { name })} onClick={() => move.by(1)}>
            <ArrowDown aria-hidden="true" /></button>
        </>}
        <button type="button" className="xo-btn-2" disabled={locked || !written}
          aria-label={tx('ui.help_centre.edit_of', { name })} onClick={onEdit}>
          <Pencil aria-hidden="true" />{tx('ui.help_centre.edit')}</button>
        <Switch on={shown} disabled={locked} label={tx('ui.help_centre.show_on_store', { name })}
          onChange={onToggle} />
      </div>}
    </div>
    {crumb && <p className="xo-help-crumb">{crumb}</p>}
    {open && (question.answer
      ? <Words text={wordsOf(question.answer, language)} className="xo-help-answer" />
      : <p className="xo-help-crumb">{tx('ui.help_centre.missing_text')}</p>)}
  </li>;
}

// The help centre is one, shared by the offices; which of its sections and
// questions an office's store shows is that office's own, so the page is one office's.
export default function XomicsHelpCentre() {
  return <ForStoreOffice title={tx('ui.help_centre.title')} className="xo-platform-page xo-help-page">
    {(store) => <HelpCentrePage store={store} />}
  </ForStoreOffice>;
}

function HelpCentrePage({ store }) {
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('CONTENT_MANAGE');
  // The languages XOmics has, and the site text's own number, which adding a
  // language's missing words asks for.
  const catalogue = useLiveResource(['translations'], useCallback(() => loadTranslationCatalogue(), []));
  const [language, setLanguage] = useState(ENGLISH);
  const list = useLiveResource(['translations'], useCallback(() => loadHelpCentre(language), [language]));
  // Where the page is: 'home' (the first page's list), a topic or a guide.
  const [place, setPlace] = useState(null);
  const [query, setQuery] = useState('');
  // The one form that is open: { kind: 'question' | 'new' | 'name' | 'texts', id }.
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [notice, setNotice] = useState(null);
  // What is being saved outside a form (a switch, an order): what it is, and
  // for a switch where it was put, so it shows that while XOmics is told.
  const [saving, setSaving] = useState(null);

  // What was read is one language's: another language chosen waits for its own.
  const data = list.data?.language === language ? list.data : null;
  const topics = useMemo(() => data?.topics ?? [], [data]);
  const home = data?.home ?? null;
  const sections = useMemo(() => {
    const byKey = new Map();
    for (const topic of topics) {
      byKey.set(`topic-${topic.id}`, { kind: 'topic', section: topic, topic });
      for (const guide of topic.guides) byKey.set(`guide-${guide.id}`, { kind: 'guide', section: guide, topic });
    }
    return byKey;
  }, [topics]);
  // Every question with where it is, for the search: by its words in this
  // language and by its English.
  const everything = useMemo(() => {
    const out = [];
    const hay = (question) => [question.question, question.answer]
      .map((text) => `${wordsOf(text, language)} ${text?.value ?? ''}`).join(' ').toLowerCase();
    const add = (groups, key, trail) => groups.forEach((group) => group.questions.forEach((question) => out.push({
      question, key, trail: [...trail, wordsOf(group.name, language)].filter(Boolean), hay: hay(question),
    })));
    if (home) {
      home.questions.forEach((question) => out.push({
        question, key: 'home', trail: [tx('ui.help_centre.home')], hay: hay(question),
      }));
    }
    for (const topic of topics) {
      const name = nameOf(topic, language);
      add(topic.groups, `topic-${topic.id}`, [name]);
      for (const guide of topic.guides) add(guide.groups, `guide-${guide.id}`, [name, nameOf(guide, language)]);
    }
    return out;
  }, [home, topics, language]);

  if (catalogue.loading && catalogue.data === null) return <Loading label={tx('ui.help_centre.loading')} />;
  if (catalogue.error) return <Failed message={catalogue.error} onRetry={catalogue.refresh} />;
  const siteText = catalogue.data.contentTypes.find((type) => type.code === SITE_TEXT);
  if (!siteText) return <Failed message={tx('ui.help_centre.no_site_text')} />;
  const languages = activeLanguages(catalogue.data);
  const english = language === ENGLISH;
  const writing = { language, contentTypeId: siteText.id, source: 'help-centre' };

  const at = place !== null && (place === 'home' ? home !== null : sections.has(place))
    ? place : (topics.length > 0 ? `topic-${topics[0].id}` : 'home');
  const current = at === 'home' ? null : sections.get(at);
  const locked = form !== null || saving !== null;

  // Leaving what is being written asks first.
  const leave = (then) => {
    if (dirty && !window.confirm(tx('ui.help_centre.discard'))) return;
    setForm(null);
    then();
  };
  const go = (key) => leave(() => { setNotice(null); setQuery(''); setPlace(key); });
  const openForm = (next) => { setNotice(null); setForm(next); };
  // A saved form stays, saying it is saving, until the list has what it saved.
  const saved = async (text) => {
    await list.refresh();
    setForm(null);
    setNotice({ tone: 'ok', text });
  };
  const formProps = {
    writing, onCancel: () => leave(() => {}), onSaved: saved, onDirtyChange: setDirty, onFailed: list.refresh,
  };

  async function change(key, work, done) {
    setNotice(null);
    setSaving(key);
    try {
      await work();
      await list.refresh();
      setNotice({ tone: 'ok', text: done });
    } catch (requestError) {
      setNotice({ tone: 'bad', text: errorMessage(requestError) });
    } finally {
      setSaving(null);
    }
  }
  const shownNow = (kind, entry) => (saving?.kind === kind && saving.id === entry.id ? saving.isActive : entry.isActive);
  const toggleQuestion = (question, isActive) => change({ kind: 'question', id: question.id, isActive },
    () => setHelpQuestionShown(question.id, isActive),
    tx(isActive ? 'ui.help_centre.now_shown' : 'ui.help_centre.now_hidden', { name: wordsOf(question.question, language) }));
  const toggleSection = (section, name, isActive) => change({ kind: 'section', id: section.id, isActive },
    () => setHelpSectionShown(section.id, isActive),
    tx(isActive ? 'ui.help_centre.now_shown' : 'ui.help_centre.now_hidden', { name }));
  const moveQuestion = (sectionId, questions, index, by) => {
    const ids = questions.map((question) => question.id);
    [ids[index], ids[index + by]] = [ids[index + by], ids[index]];
    return change({ kind: 'order', id: sectionId }, () => setHelpQuestionOrder(sectionId, ids), tx('ui.help_centre.moved'));
  };

  const row = (question, more = {}) => (form?.kind === 'question' && form.id === question.id
    ? <li key={question.id}><QuestionForm key={question.id} question={question} {...formProps} /></li>
    : <QuestionRow key={question.id} question={question} language={language} mayManage={mayManage} locked={locked}
      shown={shownNow('question', question)} onEdit={() => openForm({ kind: 'question', id: question.id })}
      onToggle={(isActive) => toggleQuestion(question, isActive)} {...more} />);

  // A list of questions with its "Add a question": a group's, or the first page's.
  const questionList = (sectionId, questions, name) => <>
    {questions.length === 0 && !(form?.kind === 'new' && form.id === sectionId)
      && <p className="xo-help-none">{tx('ui.help_centre.empty_group')}</p>}
    {questions.length > 0 && <ul className="xo-help-list">
      {questions.map((question, index) => row(question, {
        move: { first: index === 0, last: index === questions.length - 1, by: (by) => moveQuestion(sectionId, questions, index, by) },
      }))}
    </ul>}
    {form?.kind === 'new' && form.id === sectionId && <QuestionForm sectionId={sectionId} {...formProps} />}
    {mayManage && !(form?.kind === 'new' && form.id === sectionId) && (
      <button type="button" className="xo-btn-2 xo-help-add" disabled={locked}
        aria-label={tx('ui.help_centre.new_in', { name })} onClick={() => openForm({ kind: 'new', id: sectionId })}>
        <Plus aria-hidden="true" />{tx('ui.help_centre.new')}</button>
    )}
  </>;

  const groupBlock = (group) => {
    const name = wordsOf(group.name, language) || tx('ui.help_centre.missing_text');
    const shown = shownNow('section', group);
    return <section key={group.id} className={`xo-help-group${shown ? '' : ' is-off'}`} aria-label={name}>
      {form?.kind === 'name' && form.id === group.id
        ? <TextsForm id={`help-group-${group.id}`} title={tx('ui.help_centre.rename_title')}
          fields={[['name', group.name]]} {...formProps} />
        : <div className="xo-help-group-head">
          <div>
            <h3 dir="auto">{name}</h3>
            <p>{countLabel(group.questions, language)}</p>
          </div>
          {!english && group.name && ownWords(group.name, language) === ''
            && <Pill tone="info">{tx('ui.help_centre.not_translated')}</Pill>}
          {!shown && <Pill tone="flat">{tx('ui.help_centre.hidden')}</Pill>}
          {mayManage && <div className="xo-help-tools">
            <button type="button" className="xo-btn-2" disabled={locked || !group.name}
              aria-label={tx('ui.help_centre.rename_of', { name })} onClick={() => openForm({ kind: 'name', id: group.id })}>
              <Pencil aria-hidden="true" />{tx('ui.help_centre.rename')}</button>
            <Switch on={shown} disabled={locked} label={tx('ui.help_centre.show_on_store', { name })}
              onChange={(isActive) => toggleSection(group, name, isActive)} />
          </div>}
        </div>}
      {questionList(group.id, group.questions, name)}
    </section>;
  };

  // A topic's or a guide's own head: its name, whether the store shows it,
  // and its words.
  const sectionHead = ({ kind, section, topic }) => {
    const name = nameOf(section, language);
    const shown = shownNow('section', section);
    const fields = (kind === 'topic' ? TOPIC_TEXTS : GUIDE_TEXTS)
      .filter((field) => section.texts[field]).map((field) => [field, section.texts[field]]);
    return <>
      <div className="xo-help-head">
        <div>
          {kind === 'guide' && <p className="xo-help-crumb">
            <button type="button" className="xo-link" onClick={() => go(`topic-${topic.id}`)}>
              {nameOf(topic, language)}</button>
            <ChevronRight aria-hidden="true" />{tx('ui.help_centre.guide')}
          </p>}
          <h2 dir="auto">{name}</h2>
          <p>{countLabel(questionsOf(section), language)}</p>
        </div>
        {!shown && <Pill tone="flat">{tx('ui.help_centre.hidden')}</Pill>}
        {mayManage && <div className="xo-help-tools">
          <button type="button" className="xo-btn-2" disabled={locked || fields.length === 0}
            aria-label={tx('ui.help_centre.texts_of', { name })} onClick={() => openForm({ kind: 'texts', id: section.id })}>
            <Pencil aria-hidden="true" />{tx('ui.help_centre.texts')}</button>
          <Switch on={shown} disabled={locked} label={tx('ui.help_centre.show_on_store', { name })}
            onChange={(isActive) => toggleSection(section, name, isActive)} />
        </div>}
      </div>
      {!shown && <Alert tone="warn">{tx('ui.help_centre.section_hidden')}</Alert>}
      {kind === 'guide' && shown && !topic.isActive && <Alert tone="warn">{tx('ui.help_centre.topic_hidden')}</Alert>}
      {form?.kind === 'texts' && form.id === section.id && <TextsForm id={`help-section-${section.id}`}
        title={tx('ui.help_centre.texts_title')} fields={fields} {...formProps} />}
    </>;
  };

  const needle = query.trim().toLowerCase();
  const wanted = needle.split(/\s+/).filter((word) => word.length > 1);
  const found = needle === '' ? [] : everything.filter((entry) => (wanted.length > 0 ? wanted : [needle])
    .every((word) => entry.hay.includes(word)));

  return <div className="xo-platform-page xo-help-page">
    <Head title={tx('ui.help_centre.title')} under={<StoreOfficeLine store={store} />} />
    <div className="xo-product-navigation">
      <div className="xo-product-toolbar">
        <SearchField className="xo-product-search" value={query} placeholder={tx('ui.help_centre.search')}
          onChange={(value) => leave(() => { setNotice(null); setQuery(value); })} />
        <div className="xo-product-toolbar-actions">
          <SiteLanguage label={tx('ui.help_centre.language')} value={language} languages={languages} disabled={saving !== null}
            onChange={(code) => leave(() => { setNotice(null); setLanguage(code); })} />
        </div>
      </div>
    </div>
    {!mayManage && <Alert tone="info">{tx('ui.help_centre.read_only')}</Alert>}
    {notice && <p className={`xo-alert t-${notice.tone}`} role={notice.tone === 'bad' ? 'alert' : 'status'}>
      {notice.tone === 'ok' && <Check aria-hidden="true" />}{notice.text}</p>}

    {list.error && <Failed message={list.error} onRetry={list.refresh} />}
    {!list.error && data === null && <Loading label={tx('ui.help_centre.loading')} />}
    {!list.error && data !== null && (needle !== '' ? <div className="xo-help-main">
      <div className="xo-help-head"><div>
        <h2>{found.length === 0 ? tx('ui.help_centre.no_results', { query: query.trim() })
          : found.length === 1 ? tx('ui.help_centre.results_one', { query: query.trim() })
            : tx('ui.help_centre.results', { count: found.length, query: query.trim() })}</h2>
        {found.length > MAX_RESULTS && <p>{tx('ui.help_centre.results_first', { count: MAX_RESULTS })}</p>}
      </div></div>
      {found.length === 0 ? <Empty title={tx('ui.help_centre.no_results', { query: query.trim() })}
        detail={tx('ui.help_centre.no_results_detail')} />
        : <ul className="xo-help-list">
          {found.slice(0, MAX_RESULTS).map((entry) => row(entry.question, {
            crumb: <button type="button" className="xo-link" onClick={() => go(entry.key)}>{entry.trail.join(' › ')}</button>,
          }))}
        </ul>}
    </div> : <div className="xo-help-layout">
      <nav className="xo-help-side" aria-label={tx('ui.help_centre.sections')}>
        <ul>
          {home && <li><button type="button" className={at === 'home' ? 'is-on' : ''}
            aria-current={at === 'home' ? 'page' : undefined} onClick={() => go('home')}>
            <span>{tx('ui.help_centre.home')}</span><small>{home.questions.length}</small></button></li>}
          {topics.map((topic) => {
            const key = `topic-${topic.id}`;
            const openTopic = current?.topic.id === topic.id;
            return <li key={topic.id}>
              <button type="button" className={`${at === key ? 'is-on' : ''}${topic.isActive ? '' : ' is-off'}`}
                aria-current={at === key ? 'page' : undefined} onClick={() => go(key)}>
                <span dir="auto">{nameOf(topic, language)}</span><small>{questionsOf(topic).length}</small></button>
              {openTopic && topic.guides.length > 0 && <ul>
                {topic.guides.map((guide) => {
                  const guideKey = `guide-${guide.id}`;
                  return <li key={guide.id}><button type="button"
                    className={`${at === guideKey ? 'is-on' : ''}${guide.isActive ? '' : ' is-off'}`}
                    aria-current={at === guideKey ? 'page' : undefined} onClick={() => go(guideKey)}>
                    <span dir="auto">{nameOf(guide, language)}</span><small>{questionsOf(guide).length}</small></button></li>;
                })}
              </ul>}
            </li>;
          })}
        </ul>
      </nav>
      <div className="xo-help-main">
        {at === 'home' && home && <>
          <div className="xo-help-head"><div>
            <h2>{tx('ui.help_centre.home_title')}</h2>
          </div></div>
          <section className="xo-help-group" aria-label={tx('ui.help_centre.home_title')}>
            {questionList(home.id, home.questions, tx('ui.help_centre.home_title'))}
          </section>
        </>}
        {current && <>
          {sectionHead(current)}
          {current.section.groups.map(groupBlock)}
          {current.kind === 'topic' && current.section.guides.length > 0 && (
            <section className="xo-help-group" aria-label={tx('ui.help_centre.guides')}>
              <div className="xo-help-group-head"><div>
                <h3 dir="auto">{wordsOf(current.section.texts.guidesLabel, language) || tx('ui.help_centre.guides')}</h3>
              </div></div>
              <ul className="xo-help-guides">
                {current.section.guides.map((guide) => <li key={guide.id}>
                  <button type="button" className={guide.isActive ? '' : 'is-off'} onClick={() => go(`guide-${guide.id}`)}>
                    <span dir="auto">{nameOf(guide, language)}</span>
                    {!guide.isActive && <Pill tone="flat">{tx('ui.help_centre.hidden')}</Pill>}
                    <small>{countLabel(questionsOf(guide), language)}</small>
                    <ChevronRight aria-hidden="true" />
                  </button>
                </li>)}
              </ul>
            </section>
          )}
        </>}
      </div>
    </div>)}
  </div>;
}
